// FE #750: the newest found Find-and-Verify email, labelled from BE #872's `email_owner`.
// Shape: {email, status, email_owner} — from the workspace row key `_find_and_verify_email`
// (reload) or a terminal combined-email result (live run).

const FOUND_STATUSES = new Set(['verified', 'found_unverified']);
const UNVERIFIED = 'unverified — may bounce';

/** @param {{status?: string, email_owner?: {kind?: string, name?: string|null, verified?: boolean}|null}} found */
export function foundEmailLabel(found) {
  const owner = found?.email_owner;
  if (owner?.kind === 'company_inbox') return 'company inbox';
  const verified = owner ? owner.verified === true : found?.status === 'verified';
  const state = verified ? 'verified' : UNVERIFIED;
  if (owner?.kind === 'person') return `${owner.name || 'Contact'}'s email (${state})`;
  return state;
}

/** Found results of a terminal combined-email body, keyed by company id. */
export function foundEmailsFromTerminal(data) {
  const found = {};
  (data?.results || []).forEach((result) => {
    if (!result?.company_id || !result.email || !FOUND_STATUSES.has(result.status)) return;
    found[result.company_id] = { email: result.email, status: result.status, email_owner: result.email_owner ?? null };
  });
  return found;
}

/** A workspace row's hidden found-email key, or null when absent or not a found email. */
export function foundEmailFromWorkspaceRow(row) {
  const found = row?._find_and_verify_email;
  if (!found?.email || !FOUND_STATUSES.has(found.status)) return null;
  return { email: found.email, status: found.status, email_owner: found.email_owner ?? null };
}

/** The row's emails with the found address first; a differing base email stays after it. */
export function mergeFoundEmail(emails, found) {
  const list = Array.isArray(emails) ? emails : [];
  if (!found?.email) return list;
  const key = found.email.trim().toLowerCase();
  return [found.email, ...list.filter((email) => String(email).trim().toLowerCase() !== key)];
}

/**
 * Overlay this session's live results. A live entry applies only while the company still
 * carries the snapshot it was recorded against; a refreshed workspace snapshot wins.
 */
export function withLiveFoundEmails(companies, liveFoundEmails) {
  if (!Object.keys(liveFoundEmails || {}).length) return companies;
  return companies.map((company) => {
    const live = liveFoundEmails[company?.stagedId];
    if (!live || (company.foundEmail ?? null) !== live.snapshot) return company;
    return { ...company, foundEmail: live.found };
  });
}

/**
 * Emails a Verify Email run sends and charges for: the row's list minus a found email the
 * backend already verified, so it is never re-verified or charged twice.
 */
export function verifiableEmails(row, normalize) {
  const emails = normalize(row?.emails);
  const found = row?.foundEmail;
  if (found?.status !== 'verified' || !found.email) return emails;
  const key = found.email.trim().toLowerCase();
  return emails.filter((email) => email.trim().toLowerCase() !== key);
}
